import { useEffect, useState } from "react";
import { WelcomeSection } from "./dashboard/WelcomeSection";
import { TodaysPracticeCard } from "./dashboard/TodaysPracticeCard";
import { RecentTrainingSetsCard } from "./dashboard/RecentTrainingSetsCard";
import { DistanceStatsCard } from "./dashboard/DistanceStatsCard";
import { WeeklyPracticeChart } from "./dashboard/WeeklyPracticeChart";
import { UserRepository } from "@/infrastructure/repositories/UserRepository";
import { PracticeRepository } from "@/infrastructure/repositories/PracticeRepository";
import type { Workout } from "./dashboard/RecentTrainingSetsCard";
import { secureStorage } from "@/utils/secureStorage";

interface DashboardContentProps {
  onSectionChange?: (section: string, payload?: any) => void;
}

export function DashboardContent({ onSectionChange }: DashboardContentProps) {
  const login_user_name =
  secureStorage.get<string>("current_user_name") ?? "";

  const userRepo = new UserRepository();
  const practiceRepo = new PracticeRepository();

  // ---------------- STATE ----------------
  const [timeframe, setTimeframe] = useState<"week" | "month">("week");
  const [poolType, setPoolType] = useState<"SCY" | "SCM" | "LCM">("SCY");
  const [weekOffset, setWeekOffset] = useState<number>(0);

  const [summaryApiData, setSummaryApiData] = useState<any>(null);
  const [overviewApiData, setOverviewApiData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  console.log(loading)
  // ---------------- HELPERS ----------------
  const getWeekStartDate = (offset: number) => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay() + offset * 7);
    return sunday.toISOString().slice(0, 10);
  };

  const fetchWeeklyOverview = async (offset: number) => {
    try {
      setLoading(true);

      const userId = Number(
        localStorage.getItem("current_user_swimmer_id")
      );

      const weekStartDate = getWeekStartDate(offset);

      const res = await userRepo.getWeeklyPracticeSummary(
        weekStartDate,
        userId
      );

      setOverviewApiData(res.data);
    } catch (err) {
      console.error("Weekly summary fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryData = async () => {
    try {
      const userId = Number(
        localStorage.getItem("current_user_swimmer_id")
      );

      if (!userId) return;

      const weekStartDate = getWeekStartDate(0);
      const res = await userRepo.getWeeklyPracticeSummary(
        weekStartDate,
        userId
      );

      setSummaryApiData(res.data);
    } catch (err) {
      console.error("Summary data fetch failed", err);
    }
  };

  // ---------------- EFFECTS ----------------
  useEffect(() => {
    fetchWeeklyOverview(weekOffset);
  }, [weekOffset]);

  useEffect(() => {
    fetchSummaryData();
  }, []);

  useEffect(() => {
    if (!summaryApiData?.summary?.weekly?.last_week) return;

    setPoolType((prev) => {
      const lw = summaryApiData.summary.weekly.last_week;

      if (lw[prev] > 0) return prev;
      if (lw.SCY > 0) return "SCY";
      if (lw.SCM > 0) return "SCM";
      if (lw.LCM > 0) return "LCM";

      return prev;
    });
  }, [summaryApiData]);

  // ---------------- DATA MAPPING ----------------
  const summary = summaryApiData?.summary;

  const activeSummary =
    timeframe === "week"
      ? summary?.weekly
      : summary?.monthly;

  const currentDistance =
    timeframe === "week"
      ? activeSummary?.this_week?.[poolType] ?? 0
      : activeSummary?.this_month?.[poolType] ?? 0;

  const previousDistance =
    timeframe === "week"
      ? activeSummary?.last_week?.[poolType] ?? 0
      : activeSummary?.last_month?.[poolType] ?? 0;


  // ---------------- UI HELPERS ----------------
  const formatNumber = (n: number | string) =>
    typeof n === "number" ? n.toLocaleString() : n;

  const getWeekLabel = (offset: number): string => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay() + offset * 7);

    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);

    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };

    return `${sunday.toLocaleDateString(undefined, options)} - ${saturday.toLocaleDateString(
      undefined,
      options
    )}`;
  };

  const weekLabel = getWeekLabel(weekOffset);

  const getWeekDays = (offset: number) => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay() + offset * 7);

    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(sunday);
      d.setDate(sunday.getDate() + i);

      return {
        date: d.toLocaleDateString(undefined, {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        }),
        label: d.toLocaleDateString(undefined, { weekday: "short" }),
      };
    });
  };

  const weekDays = getWeekDays(weekOffset);

  // ---------------- OTHER DATA ----------------
  const [todayPractice, setTodayPractice] = useState<any>(() => {
    try {
      return JSON.parse(localStorage.getItem("today_practice") || "null");
    } catch {
      return null;
    }
  });

  const [recentLaps, setRecentLaps] = useState<any[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("recent_laps") || "[]");
    } catch {
      return [];
    }
  });

  const formatDashboardDate = (date: Date) =>
    date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

  const refreshDashboardPractice = async () => {
    const userId =
      localStorage.getItem("selected_swimmer_id") ||
      localStorage.getItem("current_user_swimmer_id") ||
      "";

    if (!userId) return;

    try {
      setDashboardLoading(true);
      const { practices, laps } = await practiceRepo.getAllPractices(String(userId));

      const yardageMap: Record<string, number> = {};
      if (Array.isArray(laps)) {
        laps.forEach((lap: any) => {
          const pid = String(
            lap.practice_id ?? lap.practiceId ?? lap.folder_practice_model_id ?? ""
          );
          if (!pid) return;
          const distance = parseInt(lap.distance) || 0;
          const reps = parseInt(lap.rep) || 1;
          yardageMap[pid] = (yardageMap[pid] || 0) + distance * reps;
        });
      }

      const todayLabel = formatDashboardDate(new Date());
      const today = practices.find((p: any) => {
        if (!p?.date) return false;
        const practiceDate = p.date instanceof Date ? p.date : new Date(p.date);
        if (Number.isNaN(practiceDate.getTime())) return false;
        return formatDashboardDate(practiceDate) === todayLabel;
      });

      if (today) {
        const practiceId = String(today.practiceId ?? today.id ?? "");
        const nextTodayPractice = {
          name: today.name || "Unnamed Practice",
          description: today.description || "",
          yardage: yardageMap[practiceId] || 0,
          practice_time: today.practice_time || "",
        };
        setTodayPractice(nextTodayPractice);
        localStorage.setItem("today_practice", JSON.stringify(nextTodayPractice));
      } else {
        setTodayPractice(null);
        localStorage.removeItem("today_practice");
      }

      const nextRecentLaps = Array.isArray(laps)
        ? laps
            .map((lap: any) => ({
              practice_id:
                lap.practice_id ?? lap.practiceId ?? lap.folder_practice_model_id,
              distance: lap.distance,
              rep: lap.rep,
              stroke: lap.stroke || "",
              createdAt: lap.createdAt || lap.created_at || "",
            }))
            .sort(
              (a: any, b: any) =>
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            )
            .slice(0, 20)
        : [];

      setRecentLaps(nextRecentLaps);
      localStorage.setItem("recent_laps", JSON.stringify(nextRecentLaps));
    } catch (error) {
      console.error("Failed to refresh dashboard practice data", error);
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    refreshDashboardPractice();
  }, []);


const dailyDistances: Record<string, number[]> =
  overviewApiData?.distance_by_day
    ? Object.fromEntries(
        Object.entries(overviewApiData.distance_by_day).map(
          ([day, value]: any) => [
            day,
            (value.distances || []).map(Number),
          ]
        )
      )
    : {};

 const recentWorkouts: Workout[] =
  Array.isArray(recentLaps) && recentLaps.length > 0
    ? recentLaps.slice(0, 5).map((lap: any): Workout => ({
        name: lap.stroke ? `${lap.stroke} Set` : "Swim Set",
        date: lap.createdAt
          ? new Date(lap.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          : "—",
        duration: lap.rep ? `${lap.rep} reps` : "",
        distance: lap.distance ? `${lap.distance}m` : "",
        intensity:
          lap.distance >= 100 && lap.rep >= 4
            ? "High"
            : lap.distance >= 50
            ? "Medium"
            : "Low",
        practiceId: lap.practice_id ? String(lap.practice_id) : undefined,
      }))
    : [];

const defaultWorkouts: Workout[] = [
  {
    name: "Sprint Intervals",
    date: "Today",
    duration: "45 min",
    distance: "2,400m",
    intensity: "High",
  },
  {
    name: "Endurance Base",
    date: "Yesterday",
    duration: "60 min",
    distance: "3,200m",
    intensity: "Medium",
  },
  {
    name: "Technique Focus",
    date: "2 days ago",
    duration: "40 min",
    distance: "1,800m",
    intensity: "Low",
  },
];

const displayWorkouts: Workout[] =
  recentWorkouts.length > 0 ? recentWorkouts : defaultWorkouts;

  const handleStartPractice = () => {
    onSectionChange?.("practice");
  };

  const handleWorkoutClick = async (workout: Workout) => {
    if (!workout.practiceId) {
      onSectionChange?.("practice");
      return;
    }

    try {
      const userId =
        localStorage.getItem("selected_swimmer_id") ||
        localStorage.getItem("current_user_swimmer_id") ||
        "";

      if (!userId) {
        onSectionChange?.("practice");
        return;
      }

      const { practices } = await practiceRepo.getAllPractices(String(userId));
      const selected = practices.find((p: any) => {
        const ids = [
          p.id,
          p.practiceId,
          (p as any).practice_id,
          (p as any).folder_practice_model_id,
        ]
          .filter(Boolean)
          .map(String);
        return ids.includes(String(workout.practiceId));
      });

      if (!selected) {
        onSectionChange?.("practice");
        return;
      }

      const selectedPractice = {
        ...selected,
        practiceId: selected.practiceId ?? selected.id,
        id: selected.practiceId ?? selected.id,
      };

      localStorage.setItem("selected_practice", JSON.stringify(selectedPractice));
      onSectionChange?.("sets");
    } catch {
      onSectionChange?.("practice");
    }
  };

  // ---------------- RENDER ----------------
  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center h-full py-16">
        <div>
          <div className="animate-spin h-10 w-10 rounded-full border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-center text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WelcomeSection
        userName={login_user_name || "User"}
        onStartPractice={handleStartPractice}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <TodaysPracticeCard
            practice={todayPractice}
            onStartPractice={handleStartPractice}
            formatNumber={formatNumber}
          />

          <RecentTrainingSetsCard
            workouts={displayWorkouts}
            onWorkoutClick={handleWorkoutClick}
          />
        </div>

        <div className="space-y-6">
          <DistanceStatsCard
            currentDistance={currentDistance}
            currentLabel={timeframe === "week" ? "This Week" : "This Month"}
            previousDistance={previousDistance}
            previousLabel={timeframe === "week" ? "Last Week" : "Last Month"}
            poolType={poolType}
            timeframe={timeframe}
            onPoolTypeChange={setPoolType}
            onTimeframeChange={setTimeframe}
          />

          <WeeklyPracticeChart
            weekLabel={weekLabel}
            weekDays={weekDays}
            dailyDistances={dailyDistances}
            onPreviousWeek={() => setWeekOffset((p) => p - 1)}
            onNextWeek={() => setWeekOffset((p) => p + 1)}
            formatNumber={formatNumber}
          />

        </div>
      </div>
    </div>
  );
}

